import React, {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {
    Flex,
    noop,
    TabsMenu,
    TabsSkinEnum,
    TabMenuItem
} from '@priority-software/priority-style-react';
import {Form} from '../../../../stores/Form/Form';
import {FormControl} from './FormControl';
import {formViewNav} from './formViewNav';
import {findDefaultTab, prepareColumnTabs, TabSection} from '../../../../stores/Form/utils';

import {FormActiveElement} from '../../../../stores/Form/Form';
import {handleFocusAction} from './actions';
import {isNavigationLocked} from '../../utils/isNavigationLocked';
import {createDataTestId} from '../../../../utils/dataTestId';
import styles from './FormView.module.scss';

type Props = {
    currentForm: Form;
    isActiveForm: boolean;
    sectionClass?: string;
    componentClasses?: string;
};

export const FormView = observer((props: Props) => {
    const {currentForm, isActiveForm, sectionClass, componentClasses} = props;
    const {searchModeLevel, currentRow, columnsTabs, currentColumnId, records} = currentForm;
    const [mappedColumnsTabs] = useState(prepareColumnTabs(currentForm, columnsTabs));
    const [activeTab, setActiveTab] = useState(findDefaultTab(columnsTabs, currentColumnId));

    const onTabChange = useCallback(
        async (id: string) => {
            const tabColumn = columnsTabs.find(column => column.TabId === Number(id));
            const newActiveCol = currentForm.getColumnByID(tabColumn.ColumnId);

            if (newActiveCol) {
                const isAllowed = await handleFocusAction(currentForm, newActiveCol.index);

                if (isAllowed) {
                    currentForm.setCurrentColumnId(newActiveCol.id);
                    setActiveTab(Number(id));
                }
            }
        },
        [currentForm, columnsTabs]
    );

    const onFocus = useCallback(
        async (columnIndex: number, el: FormActiveElement) => {
            currentForm.setFormActiveElement(el);
            await handleFocusAction(currentForm, columnIndex);
        },
        [currentForm]
    );

    const currentRowData = records[currentRow];

    const renderControls = (tabSection?: TabSection) => {
        if (!tabSection) return null;

        return tabSection.Controls.map(({Column}) => {
            const currField = currentRowData?.getField(Column.id);
            const searchValue = currentForm.searchModeData[currentRow]?.[Column.id];

            return (
                <FormControl
                    testId={`column-name-${Column.Name}`}
                    key={`${currentForm?.currentRecord?.clientKey}-${Column.id}-${currentForm.searchModeLevel}`}
                    updateSearchValue={currentForm.updateSearchModeCellValue}
                    searchValue={searchValue}
                    column={Column}
                    searchMode={!!searchModeLevel}
                    isActive={isActiveForm && Column.id === currentColumnId}
                    onFocus={onFocus}
                    currentRow={currentRow}
                    currField={currField}
                />
            );
        });
    };

    useEffect(() => {
        if (isActiveForm) {
            const handleKeydown = async e => {
                if (currentForm && !isNavigationLocked(currentForm)) {
                    formViewNav(e, currentForm, activeTab, async navData => {
                        const column = currentForm.getColumnByID(navData.ColumnId);

                        const isAllowed = await handleFocusAction(currentForm, column.index);

                        if (isAllowed) {
                            // currentForm.setCurrentColumnId(navData.ColumnId);
                            if (navData.ActiveTab && activeTab !== navData.ActiveTab) {
                                setActiveTab(navData.ActiveTab);
                            }
                        }
                    });
                }
            };

            window.addEventListener('keydown', handleKeydown, true);
            return () => window.removeEventListener('keydown', handleKeydown, true);
        }
        return noop;
    }, [isActiveForm, activeTab]);

    const tabData = mappedColumnsTabs.find(tab => tab.TabId === activeTab);
    const leftData = tabData?.TabSection[0];
    const rightData = tabData?.TabSection[1];

    return (
        <div
            className={cx(styles.component, componentClasses)}
            key={currentRowData?.clientKey}
            {...createDataTestId('form-single-view-container', false)}
        >
            <div
                className={cx(styles.mainArea, sectionClass)}
                {...createDataTestId('form-single-view-main-area', false)}
            >
                <div className={styles.fieldSet}>
                    {renderControls(mappedColumnsTabs[0]?.TabSection?.[0])}
                </div>
            </div>

            <div className={cx(styles.additionalArea, sectionClass)}>
                <div className={styles.tabsWrap}>
                    <TabsMenu
                        skin={TabsSkinEnum.material}
                        {...createDataTestId('tabs-menu-container', true)}
                    >
                        {mappedColumnsTabs.map(tabLayout => {
                            if (!tabLayout.TabId) return null;

                            const tabID = tabLayout.TabId;

                            return (
                                <TabMenuItem
                                    key={tabID}
                                    id={tabID.toString()}
                                    onTabClick={onTabChange}
                                    text={tabLayout.TabTitle}
                                    isActive={tabID === activeTab}
                                    componentClasses={styles.tabItem}
                                />
                            );
                        })}
                    </TabsMenu>
                </div>

                {tabData && (
                    <Flex alignItems="flex-start">
                        <div
                            className={styles.fieldSet}
                            {...createDataTestId('form-single-view-column', false)}
                        >
                            {renderControls(leftData)}
                        </div>

                        {rightData && (
                            <div
                                className={styles.fieldSet}
                                {...createDataTestId('form-single-view-column', false)}
                            >
                                {renderControls(rightData)}
                            </div>
                        )}
                    </Flex>
                )}
            </div>
        </div>
    );
});
